import React, { useState } from 'react';
import { RouteComponentProps, useParams, withRouter } from 'react-router';
import { Helmet } from 'react-helmet-async';
import { axiosKK } from '../services/networkRequest';
import errorMessage from '../services/errorMessage';
import SubmitButton from '../components/SubmitButton';
import { Customer, UserResponse } from '../models/APIModels';

interface ResetPasswordParams {
  token: string;
}

function ResetPasswordComponent({ history }: RouteComponentProps): JSX.Element {
  const { token } = useParams<ResetPasswordParams>();
  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [successMessage, setSuccessMessage] = useState<string>();
  const [networkError, setNetworkError] = useState<string>();
  const [fieldErrors, setFieldErrors] = useState<{ password?: string; passwordConfirmation?: string }>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (): boolean => {
    const errors: { password?: string; passwordConfirmation?: string } = {};
    let valid = true;

    if (!token) {
        setNetworkError('Invalid or missing token');
        valid = false;
    }

    if (password.length < 8) {
      errors.password = 'Password too short';
      valid = false;
    }

    if (password !== passwordConfirmation) {
      errors.passwordConfirmation = "Password and confirmation don't match";
      valid = false;
    }

    setFieldErrors(errors);
    return valid;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    setNetworkError(undefined);

    axiosKK
      .post<{ customer?: Customer; user?: UserResponse }>(
        '/resetPassword',
        { password, passwordConfirmation, token },
      )
      .then(({ data }) => {
        if (data.user) {
          history.push('/admin/dashboard');
        } else {
          history.push('/dashboard');
        }
        setSuccessMessage('Password reset successful');
      })
      .catch((error) => {
        errorMessage(setNetworkError)(error);
        setIsSubmitting(false);
      });
  };

  return (
    <div className="auth-page">
      <Helmet>
        <title>Reset Password</title>
      </Helmet>
      
      <div className="auth-card">
        <div className="auth-card__header">
          <h3>Reset Password</h3>
          <p>Choose a new password for your account</p>
        </div>

        {networkError && (
          <div className="auth-card__error">{networkError}</div>
        )}
        
        {successMessage && (
            <div className="auth-card__success">{successMessage}</div>
        )}

        <form className="auth-card__form" onSubmit={handleSubmit}>
            <div className="auth-card__row">
                <div className="auth-card__field">
                  <label htmlFor="password">Password</label>
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Min. 8 characters"
                  />
                  {fieldErrors.password && (
                    <span className="field-error">{fieldErrors.password}</span>
                  )}
                </div>

                <div className="auth-card__field">
                  <label htmlFor="passwordConfirmation">Confirm Password</label>
                  <input
                    id="passwordConfirmation"
                    type="password"
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    placeholder="Confirm your password"
                  />
                  {fieldErrors.passwordConfirmation && (
                    <span className="field-error">{fieldErrors.passwordConfirmation}</span>
                  )}
                </div>
            </div>

            <SubmitButton disabled={isSubmitting} isSubmitting={isSubmitting}>
              Reset Password
            </SubmitButton>
        </form>

        <div className="auth-card__toggle">
          <p>
             <button type="button" onClick={() => history.push('/login')}>
               Back to Login
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}

export default withRouter(ResetPasswordComponent);
